import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import './admin-panel.css';

const STATUS_LABEL = {
  pending_payment: 'Pending Payment',
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
};

export default function VaccinationApplicationsTab() {
  const [applications, setApplications] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  const load = () => {
    setStatus('loading');
    axios
      .get('/api/vaccination-applications/', { params: { status: 'all' } })
      .then((response) => {
        setApplications(response.data?.applications ?? []);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  };

  useEffect(load, []);

  const updateStatus = async (application, newStatus) => {
    try {
      await axios.post(`/api/vaccination-applications/?id=${application.id}&_method=PATCH`, { status: newStatus });
      toast.success(`Application #${application.id} marked as ${STATUS_LABEL[newStatus]}.`);
      load();
    } catch {
      toast.error('Could not update the application. Please try again.');
    }
  };

  return (
    <div>
      <div className="admin-topbar">
        <div>
          <h1>Vaccination Certificate Applications</h1>
          <p>Review submitted applications, verify payment, and approve or reject certificates.</p>
        </div>
      </div>

      {status === 'loading' && (
        <div className="admin-panel admin-empty-state">
          <p>Loading…</p>
        </div>
      )}

      {status === 'error' && (
        <div className="admin-panel admin-empty-state">
          <p>Could not load applications. Please try again later.</p>
        </div>
      )}

      {status === 'ready' && applications.length === 0 && (
        <div className="admin-panel admin-empty-state">
          <i className="fa fa-inbox" />
          <p>No vaccination certificate applications yet.</p>
        </div>
      )}

      {status === 'ready' &&
        applications.map((app) => (
          <div className="admin-review-card" key={app.id}>
            <div className="admin-review-thumb-wrap">
              <img src={app.photo} alt={app.fullName} className="admin-review-thumb" />
            </div>
            <div className="admin-review-body">
              <span className={`admin-status-pill ${app.status === 'approved' ? 'published' : 'review'}`}>
                {STATUS_LABEL[app.status] || app.status}
              </span>
              <h3 style={{ marginTop: 10 }}>
                {app.fullName} — {app.vaccine}
              </h3>
              <div className="admin-review-meta">
                <span>
                  <i className="fa fa-id-card" />
                  CNIC: {app.cnic}
                </span>
                <span>
                  <i className="fa fa-passport" />
                  Passport: {app.passportNumber}
                </span>
                <span>
                  <i className="fa fa-envelope" />
                  {app.email}
                </span>
                <span>
                  <i className="fa fa-phone" />
                  {app.phone}
                </span>
                <span>
                  <i className="fa fa-money-bill" />
                  Rs. {app.fee?.toLocaleString?.() ?? app.fee}
                </span>
              </div>
              {app.paymentScreenshot && (
                <div className="mb-3">
                  <div className="text-muted mb-1" style={{ fontSize: '0.8rem' }}>Payment Screenshot</div>
                  <a href={app.paymentScreenshot} target="_blank" rel="noopener noreferrer">
                    <img
                      src={app.paymentScreenshot}
                      alt="Payment screenshot"
                      style={{ maxWidth: 160, maxHeight: 160, borderRadius: 6, border: '1px solid #e2e6ea' }}
                    />
                  </a>
                </div>
              )}
              <div className="admin-review-actions">
                {app.status !== 'approved' && (
                  <button
                    className="admin-btn admin-btn-success admin-btn-sm"
                    onClick={() => updateStatus(app, 'approved')}
                  >
                    <i className="fa fa-check" /> Approve
                  </button>
                )}
                {app.status !== 'rejected' && (
                  <button
                    className="admin-btn admin-btn-danger admin-btn-sm"
                    onClick={() => updateStatus(app, 'rejected')}
                  >
                    <i className="fa fa-times" /> Reject
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
    </div>
  );
}
